<div class="step-content">
  <div class="profile-form">
    <!-- Row 1: Title & Access Policy -->
    <div class="form-row">
      <div class="form-group">
        <label for="title" class="form-label">Title <span class="required-asterisk">*</span></label>
        <input 
          type="text" 
          id="title" 
          class="form-input"
          [class.form-input--error]="hasError('title') || titleAvailabilityStatus === 'not-available'"
          placeholder="Enter title"
          [value]="formData.title"
          (input)="onTitleChange($any($event.target).value)"
        />
        <span *ngIf="hasError('title')" class="error-message">{{ getError('title') }}</span>
        <!-- Title Availability Status -->
        <div *ngIf="formData.title" class="title-availability-message">
          <div *ngIf="isCheckingTitle" class="availability-checking">Checking availability...</div>
          <div *ngIf="!isCheckingTitle && titleAvailabilityStatus === 'available'" class="availability-available">
            <svg class="availability-icon" width="18" height="18" viewBox="0 0 18 18" fill="none">
              <circle cx="9" cy="9" r="8" fill="#4caf50" stroke="#4caf50" stroke-width="1"/>
              <path d="M6 9L8 11L12 7" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Title is available
          </div>
          <div *ngIf="!isCheckingTitle && titleAvailabilityStatus === 'not-available'" class="availability-not-available">
            <svg class="availability-icon" width="18" height="18" viewBox="0 0 18 18" fill="none">
              <circle cx="9" cy="9" r="8" fill="#d32f2f" stroke="#d32f2f" stroke-width="1"/>
              <path d="M6 6L12 12M12 6L6 12" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Title already exists
          </div>
        </div>
      </div>
      <div class="form-group">
        <label for="accessPolicy" class="form-label">Access Policy <span class="required-asterisk">*</span></label>
        <div #accessPolicyWrapper class="custom-dropdown-wrapper">
          <div
            class="custom-dropdown-trigger form-input"
            [class.form-input--error]="hasError('accessPolicy')"
            [class.open]="isAccessPolicyDropdownOpen"
            (click)="toggleAccessPolicyDropdown()"
          >
            <div class="trigger-content">
              <span
                *ngIf="!formData.accessPolicy || formData.accessPolicy === ''"
                class="placeholder"
              >
                Select Access Policy
              </span>
              <span
                *ngIf="formData.accessPolicy && formData.accessPolicy !== ''"
                class="access-policy-chip"
                [ngClass]="'access-policy-chip--' + (formData.accessPolicy || 'public')"
              >
                <img src="/public.svg" alt="" class="access-policy-chip__icon" aria-hidden="true" />
                {{ getSelectedAccessPolicyLabel() }}
              </span>
            </div>
            <svg
              class="dropdown-arrow"
              [class.rotated]="isAccessPolicyDropdownOpen"
              width="12"
              height="8"
              viewBox="0 0 12 8"
              fill="none"
            >
              <path d="M1 1L6 6L11 1" stroke="#666" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </div>
          <div
            *ngIf="isAccessPolicyDropdownOpen"
            class="custom-dropdown-panel"
            (click)="$event.stopPropagation()"
          >
            <div
              *ngFor="let option of accessPolicyOptions"
              class="dropdown-option"
              [class.selected]="isAccessPolicySelected(option.value)"
              [class.dropdown-option--disabled]="isAccessPolicyDisabled(option)"
              (click)="selectAccessPolicy(option)"
            >
              <div class="checkbox-wrapper">
                <input
                  type="checkbox"
                  [checked]="isAccessPolicySelected(option.value)"
                  [disabled]="isAccessPolicyDisabled(option)"
                  class="checkbox-custom"
                  readonly
                />
              </div>
              <span class="option-label">{{ option.label }}</span>
            </div>
          </div>
        </div>
        <span *ngIf="hasError('accessPolicy')" class="error-message">{{ getError('accessPolicy') }}</span>
      </div>
    </div>

    <!-- Row 2: Short Description & Long Description -->
    <div class="form-row">
      <div class="form-group">
        <label for="shortDescription" class="form-label">Short Description <span class="required-asterisk">*</span></label>
        <textarea 
          id="shortDescription" 
          class="form-input form-input--textarea"
          [class.form-input--error]="hasError('shortDescription')"
          placeholder="Enter short description"
          rows="4"
          [value]="formData.shortDescription"
          (input)="onShortDescriptionChange($any($event.target).value)"
        ></textarea>
        <span *ngIf="hasError('shortDescription')" class="error-message">{{ getError('shortDescription') }}</span>
      </div>
      <div class="form-group">
        <label for="description" class="form-label">Long Description <span class="required-asterisk">*</span></label>
        <textarea 
          id="description" 
          class="form-input form-input--textarea"
          [class.form-input--error]="hasError('description')"
          placeholder="Enter long description"
          rows="4"
          [value]="formData.description"
          (input)="onDescriptionChange($any($event.target).value)"
        ></textarea>
        <span *ngIf="hasError('description')" class="error-message">{{ getError('description') }}</span>
      </div>
    </div>

    <!-- Row 3: Industry & Organisation Type -->
    <div class="form-row">
      <div class="form-group">
        <label for="industry" class="form-label">Industry <span class="required-asterisk">*</span></label>
        <app-multiselect
          id="industry"
          [ngModel]="formData.selectedIndustries"
          (ngModelChange)="onIndustriesChange($event)"
          [options]="industryOptions"
          optionLabel="label"
          placeholder="Select Industry"
          display="chip"
          [showClear]="true"
          [styleClass]="hasError('industry') ? 'form-multiselect form-multiselect--error' : 'form-multiselect'"
          (onTriggerMousedown)="onMultiselectTriggerMousedown($event)"
          (onOpen)="onMultiselectOpen()"
        ></app-multiselect>
        <span *ngIf="hasError('industry')" class="error-message">{{ getError('industry') }}</span>
      </div>
      <div class="form-group">
        <label for="organisationType" class="form-label">Organisation Type <span class="required-asterisk">*</span></label>
        <app-multiselect
          id="organisationType"
          [ngModel]="formData.selectedOrganisationTypes"
          (ngModelChange)="onOrganisationTypesChange($event)"
          [options]="organisationTypeOptions"
          optionLabel="label"
          placeholder="Select Organisation Type"
          display="chip"
          [showClear]="true"
          [styleClass]="hasError('organisationType') ? 'form-multiselect form-multiselect--error' : 'form-multiselect'"
          (onTriggerMousedown)="onMultiselectTriggerMousedown($event)"
          (onOpen)="onMultiselectOpen()"
        ></app-multiselect>
        <span *ngIf="hasError('organisationType')" class="error-message">{{ getError('organisationType') }}</span>
      </div>
    </div>

    <!-- Row 4: Tags -->
    <div class="form-row">
      <div class="form-group">
        <label for="tags" class="form-label">Tags <span class="required-asterisk">*</span></label>
        <app-tag-input
          id="tags"
          [ngModel]="formData.tags"
          (ngModelChange)="onTagsChange($event)"
          placeholder="Enter a tag"
          [styleClass]="hasError('tags') ? 'tag-input--error' : ''"
        ></app-tag-input>
        <span *ngIf="hasError('tags')" class="error-message">{{ getError('tags') }}</span>
      </div>
    </div>
  </div>
</div>
